import React, { useState, useEffect } from 'react';
import { Card, CardBody, Row, Col, Spinner } from "reactstrap";
import ReactApexChart from 'react-apexcharts';
import "./dashboard.scss";
import { API_BASE_URL } from '../../constants';
import axios from "axios";
import { toast } from 'react-toastify';

const AttendanceAnalytics = () => {
    const [series, setSeries] = useState([0, 0, 0, 0]); // Present, Absent, Leave, Half-day
    const [stats, setStats] = useState({
        present: 0,
        absent: 0,
        leave: 0,
        half_day: 0,
        late: 0,
        total_staff: 0
    });
    const [timeRange, setTimeRange] = useState('today');
    const [category, setCategory] = useState('staff');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Chart configuration for Donut Chart
    const options = {
        labels: ["Present", "Absent", "On Leave", "Half Day"],
        plotOptions: {
            pie: {
                donut: {
                    size: '75%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total',
                            formatter: () => stats.total_staff.toString()
                        }
                    }
                }
            }
        },
        dataLabels: {
            enabled: false
        },
        legend: {
            position: 'bottom',
            horizontalAlign: 'center'
        },
        colors: ['#5664d2', '#f46a6a', '#f1b44c', '#34c38f'],
    };

    const getCurrentDate = () => {
        const today = new Date();
        // Format as YYYY-MM-DD
        return today.toISOString().split('T')[0];
    };

    const fetchAttendanceData = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error('No authentication token found');
            }

            const date = getCurrentDate();
            const endpoint = `${API_BASE_URL}/${category}-attendance/analysis`;

            const response = await axios.get(endpoint, {
                params: {
                    date: date,
                    range: timeRange
                },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                },
            });

            const data = response.data;

            if (data.status === "success") {
                const attendanceData = data.data;
                setStats({
                    present: attendanceData.present,
                    absent: attendanceData.absent,
                    leave: attendanceData.leave,
                    half_day: attendanceData.half_day,
                    late: attendanceData.late,
                    total_staff: attendanceData.total_staff
                });
                setSeries([
                    attendanceData.present,
                    attendanceData.absent,
                    attendanceData.leave,
                    attendanceData.half_day
                ]);
            } else {
                throw new Error(data.message || 'Failed to fetch attendance data');
            }
        } catch (error) {
            console.error("Error fetching attendance data:", error);
            const errorMsg = error.response?.data?.message ||
                error.message ||
                'Failed to fetch attendance data';
            setError(errorMsg);
            toast.error(`Error: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    }, [timeRange, category]);

    useEffect(() => {
        fetchAttendanceData();
    }, [fetchAttendanceData]);

    const handleTimeRangeChange = (e) => {
        setTimeRange(e.target.value);
    };

    const handleCategoryChange = (e) => {
        setCategory(e.target.value);
    };

    return (
        <React.Fragment>
            <Card>
                <CardBody>
                    <div className="float-end">
                        <select
                            className="form-select form-select-sm"
                            value={timeRange}
                            onChange={handleTimeRangeChange}
                            disabled={loading}
                        >
                            <option value="today">Today</option>
                            <option value="week">This week</option>
                            <option value="month">This Month</option>
                            <option value="year">Current year</option>
                        </select>
                    </div>
                    <h4 className="card-title mb-4">Attendance Analytics</h4>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <p className="mt-2">Loading attendance data...</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-5 text-danger">
                            <i className="mdi mdi-alert-circle-outline font-size-24"></i>
                            <p className="mt-2">{error}</p>
                            <button
                                className="btn btn-sm btn-primary mt-2"
                                onClick={fetchAttendanceData}
                            >
                                Retry
                            </button>
                        </div>
                    ) : (
                        <>
                            <div id="donut-chart" className="apex-charts">
                                <ReactApexChart
                                    options={options}
                                    series={series}
                                    type="donut"
                                    height={350}
                                />
                            </div>

                            <Row className="mt-3">
                                <Col xs={4}>
                                    <select
                                        className="form-select form-select-sm"
                                        value={category}
                                        onChange={handleCategoryChange}
                                        disabled={loading}
                                    >
                                        <option value="staff">Staff</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </Col>
                                <Col xs={8}>
                                    <Row>
                                        <Col xs={6} md={3}>
                                            <div className="text-center mt-3">
                                                <p className="mb-1 text-truncate">
                                                    <i className="mdi mdi-circle text-primary font-size-10 me-1"></i> Present
                                                </p>
                                                <h5>{stats.present}</h5>
                                                <small className="text-muted">{((stats.present / stats.total_staff) * 100 || 0).toFixed(1)}%</small>
                                            </div>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <div className="text-center mt-3">
                                                <p className="mb-1 text-truncate">
                                                    <i className="mdi mdi-circle text-danger font-size-10 me-1"></i> Absent
                                                </p>
                                                <h5>{stats.absent}</h5>
                                                <small className="text-muted">{((stats.absent / stats.total_staff) * 100 || 0).toFixed(1)}%</small>
                                            </div>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <div className="text-center mt-3">
                                                <p className="mb-1 text-truncate">
                                                    <i className="mdi mdi-circle text-warning font-size-10 me-1"></i> On Leave
                                                </p>
                                                <h5>{stats.leave}</h5>
                                                <small className="text-muted">{((stats.leave / stats.total_staff) * 100 || 0).toFixed(1)}%</small>
                                            </div>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <div className="text-center mt-3">
                                                <p className="mb-1 text-truncate">
                                                    <i className="mdi mdi-circle text-success font-size-10 me-1"></i> Half Day
                                                </p>
                                                <h5>{stats.half_day}</h5>
                                                <small className="text-muted">{((stats.half_day / stats.total_staff) * 100 || 0).toFixed(1)}%</small>
                                            </div>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </>
                    )}
                </CardBody>
            </Card>
        </React.Fragment>
    );
};

export default AttendanceAnalytics;